import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import {
  FlaskConical, Plus, Loader2, Trash2, Barcode, ClipboardCheck, ShieldCheck, Send, Activity, TestTube, FileDown,
} from "lucide-react";

const STATUS = {
  solicitado: { label: "Solicitado", cls: "bg-chart-3/12 text-chart-3 border-chart-3/25" },
  coletado: { label: "Coletado", cls: "bg-warning/12 text-warning border-warning/25" },
  em_analise: { label: "Em análise", cls: "bg-ai/12 text-ai border-ai/25" },
  validado: { label: "Validado", cls: "bg-primary/12 text-primary border-primary/25" },
  liberado: { label: "Liberado", cls: "bg-success/12 text-success border-success/25" },
};

export default function LabModule({ standalone = false }) {
  const { user } = useAuth();
  const isClinic = ["medico", "admin"].includes(user?.role);

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [catalog, setCatalog] = useState({});
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState("todos");

  const load = () => {
    api.get("/lab/orders").then((r) => setOrders(r.data));
    api.get("/lab/stats").then((r) => setStats(r.data));
  };
  useEffect(() => {
    load();
    api.get("/catalog").then((r) => setCatalog(r.data));
    if (isClinic) api.get("/patients").then((r) => setPatients(r.data));
  }, []); // eslint-disable-line

  const filtered = filter === "todos" ? orders : orders.filter((o) => o.status === filter);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const content = (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">LIA Lab</p>
          <h1 className="font-heading text-4xl font-light tracking-tighter">Fluxo laboratorial</h1>
          <p className="text-muted-foreground text-sm mt-1">Fila de exames, coleta, resultados, validação e laudo.</p>
        </div>
        <div className="flex gap-2">
          {isClinic && <NewOrderDialog patients={patients} catalog={catalog} onDone={load} />}
        </div>
      </div>

      {/* Stats por status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lab-stats">
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(filter === k ? "todos" : k)}
            data-testid={`lab-stat-${k}`}
            className={`text-left p-4 rounded-lg border transition-colors ${filter === k ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
            <div className="font-mono-data text-2xl font-semibold">{stats[k] ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">{v.label}</div>
          </button>
        ))}
      </div>

      {/* Fila */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed" data-testid="lab-empty">
          <TestTube className="w-8 h-8 mx-auto mb-3 opacity-40" />
          {filter === "todos" ? "Nenhum pedido no laboratório." : `Nenhum pedido no status "${STATUS[filter]?.label}".`}
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <Card key={o.id} className="p-5 border-border shadow-none" data-testid={`lab-order-${o.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-medium">{o.patient_name}</h4>
                    <Badge variant="outline" className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label}</Badge>
                    {o.priority !== "rotina" && <Badge variant="outline" className="border-destructive/30 text-destructive">{o.priority}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {o.tests?.map((t) => t.panel_label).join(", ")}
                  </p>
                  {o.barcode && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 font-mono-data">
                      <Barcode className="w-3.5 h-3.5" /> {o.barcode}{o.sector ? ` · ${o.sector}` : ""}
                    </p>
                  )}
                  {o.validation && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5 text-success" /> Validado por {o.validation.validated_by} · hash {o.validation.hash?.slice(0, 12)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {o.status === "solicitado" && (
                    <Button size="sm" onClick={() => act(() => api.post(`/lab/orders/${o.id}/collect`, { sector: "Bancada 1" }), "Coleta registrada — código gerado")} data-testid={`lab-collect-${o.id}`}>
                      <Barcode className="w-4 h-4 mr-1" /> Registrar coleta
                    </Button>
                  )}
                  {(o.status === "coletado" || o.status === "em_analise") && (
                    <ResultsDialog order={o} catalog={catalog} onDone={load} />
                  )}
                  {o.status === "em_analise" && o.results?.length > 0 && (
                    <ValidateDialog order={o} onDone={load} />
                  )}
                  {o.status === "validado" && (
                    <Button size="sm" onClick={() => act(() => api.post(`/lab/orders/${o.id}/release`), "Laudo liberado — resultados no prontuário")} data-testid={`lab-release-${o.id}`}>
                      <Send className="w-4 h-4 mr-1" /> Liberar laudo
                    </Button>
                  )}
                  {(o.status === "validado" || o.status === "liberado") && (
                    <Button size="sm" variant="outline" data-testid={`lab-laudo-pdf-${o.id}`}
                      onClick={async () => {
                        try {
                          const res = await api.get(`/lab/orders/${o.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          window.open(url, "_blank", "noopener");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch { toast.error("Não foi possível gerar o laudo"); }
                      }}>
                      <FileDown className="w-4 h-4 mr-1" /> Laudo PDF
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/lab/orders/${o.id}`), "Pedido removido")} data-testid={`lab-delete-${o.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>

              {o.results?.length > 0 && (
                <div className="mt-4 border-t border-border pt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                  {o.results.flatMap((r) => r.measurements.map((m) => (
                    <div key={`${r.panel_key}-${m.marker_key}`} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{m.label}</span>
                      <span className="flex items-center gap-2"><span className="font-mono-data">{m.value} {m.unit}</span><StatusBadge status={m.status} /></span>
                    </div>
                  )))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (!standalone) return <div className="p-6 sm:p-10 max-w-6xl" data-testid="lab-module">{content}</div>;
  return <div className="p-6 sm:p-10 max-w-6xl" data-testid="lab-module">{content}</div>;
}

function NewOrderDialog({ patients, catalog, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [priority, setPriority] = useState("rotina");
  const [tests, setTests] = useState([]);
  const [notes, setNotes] = useState("");

  const toggle = (k) => setTests((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const save = async () => {
    if (!patientId) { toast.error("Selecione o paciente"); return; }
    if (!tests.length) { toast.error("Selecione ao menos um exame"); return; }
    setSaving(true);
    try {
      await api.post("/lab/orders", { patient_id: patientId, tests, priority, notes: notes || null });
      toast.success("Pedido criado");
      setOpen(false); setPatientId(""); setTests([]); setNotes(""); setPriority("rotina");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="lab-new-order-btn"><Plus className="w-4 h-4 mr-2" /> Novo pedido</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lab-order-dialog">
        <DialogHeader>
          <DialogTitle>Novo pedido de exames</DialogTitle>
          <DialogDescription>Selecione o paciente e os painéis a realizar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Paciente</Label>
              <Select value={patientId} onValueChange={setPatientId}>
                <SelectTrigger data-testid="lab-order-patient"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Prioridade</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="lab-order-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rotina">Rotina</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Exames</Label>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-border rounded-md p-3">
              {Object.entries(catalog).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`lab-test-${k}`}>
                  <Checkbox checked={tests.includes(k)} onCheckedChange={() => toggle(k)} />
                  {v.label}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2"><Label>Observações</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lab-order-notes" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="lab-order-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Criar pedido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ order, catalog, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vals, setVals] = useState({});

  const panels = useMemo(() => order.tests.map((t) => ({ key: t.panel_key, ...(catalog[t.panel_key] || {}) })).filter((p) => p.markers), [order, catalog]);

  const setVal = (pk, mk, v) => setVals((prev) => ({ ...prev, [`${pk}|${mk}`]: v }));

  const save = async () => {
    const results = [];
    for (const p of panels) {
      const measurements = [];
      for (const m of p.markers) {
        const v = vals[`${p.key}|${m.key}`];
        if (v !== undefined && v !== "") measurements.push({ marker_key: m.key, value: parseFloat(v) });
      }
      if (measurements.length) results.push({ panel_key: p.key, measurements });
    }
    if (!results.length) { toast.error("Preencha ao menos um resultado"); return; }
    setSaving(true);
    try {
      await api.post(`/lab/orders/${order.id}/results`, { results });
      toast.success("Resultados registrados");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`lab-results-btn-${order.id}`}>
          <ClipboardCheck className="w-4 h-4 mr-1" /> Lançar resultados
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lab-results-dialog">
        <DialogHeader>
          <DialogTitle>Resultados — {order.patient_name}</DialogTitle>
          <DialogDescription>Insira os valores medidos. A faixa de referência avalia o status automaticamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {panels.map((p) => (
            <div key={p.key}>
              <h4 className="font-heading font-medium text-sm mb-2">{p.label}</h4>
              <div className="grid grid-cols-2 gap-3">
                {p.markers.map((m) => (
                  <div key={m.key} className="space-y-1">
                    <Label className="text-xs">{m.label} <span className="text-muted-foreground">({m.unit})</span></Label>
                    <Input type="number" step="any" value={vals[`${p.key}|${m.key}`] ?? ""}
                      onChange={(e) => setVal(p.key, m.key, e.target.value)}
                      placeholder={`${m.min}–${m.max}`} data-testid={`lab-res-${p.key}-${m.key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="lab-results-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar resultados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ValidateDialog({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/lab/orders/${order.id}/validate`, { technical_notes: notes || null });
      toast.success("Validado tecnicamente");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`lab-validate-btn-${order.id}`}><ShieldCheck className="w-4 h-4 mr-1" /> Validar</Button>
      </DialogTrigger>
      <DialogContent data-testid="lab-validate-dialog">
        <DialogHeader>
          <DialogTitle>Validação técnica</DialogTitle>
          <DialogDescription>Assinatura técnica dos resultados de {order.patient_name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Notas técnicas (opcional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lab-validate-notes" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="lab-validate-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Validar e assinar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
